import { createFileRoute } from "@tanstack/react-router";
import { Suspense, useMemo, useState } from "react";
import type { RowSelectionState } from "@tanstack/react-table";
import { useLocale, useTranslations } from "next-intl";
import { PageHeader } from "@/components/page-header";
import { RequestsFilters } from "@/components/requests/requests-filters";
import { RequestsTable } from "@/components/requests/requests-table";
import { PromoteRequestDialog } from "@/components/requests/promote-request-dialog";
import { PromoteBulkDialog } from "@/components/requests/promote-bulk-dialog";
import { Button } from "@/components/ui/button";
import { useRequestsSearch } from "@/hooks/use-requests-search";
import { useExport } from "@/hooks/use-export";
import { requestsExportColumns } from "@/lib/export/columns";
import { useRouter } from "@/i18n/navigation";
import type { SearchRequestItem, RequestType } from "@/types";
import type { ApiSearchRequestItem } from "@/types/api-helpers";

export const Route = createFileRoute("/_dashboard/requests/")({ component: RequestsPage });

function RequestsContent() {
  const t = useTranslations("RequestsPage");
  const tPromote = useTranslations("PromoteRequest");
  const tRequestStatus = useTranslations("RequestStatus");
  const tRequestType = useTranslations("RequestType");
  const tRequestsTable = useTranslations("RequestsTable");
  const locale = useLocale();
  const router = useRouter();
  const {
    data,
    isLoading,
    search,
    setSearch,
    status,
    setStatus,
    type,
    setType,
    createdAfter,
    setCreatedAfter,
    createdBefore,
    setCreatedBefore,
    sorting,
    onSortingChange,
    paginationState,
    onPaginationChange,
    pageCount,
    refetch,
  } = useRequestsSearch();

  const exportFilters = useMemo(() => ({
    ...(search ? { q: search } : {}),
    ...(status !== "all" ? { status } : {}),
    ...(type !== "all" ? { type } : {}),
    ...(createdAfter ? { created_after: `${createdAfter}T00:00:00Z` } : {}),
    ...(createdBefore ? { created_before: `${createdBefore}T23:59:59Z` } : {}),
  }), [search, status, type, createdAfter, createdBefore]);

  const { exportData, progress: exportProgress, cancel: cancelExport } = useExport<ApiSearchRequestItem>({
    endpoint: "/admin/search/requests",
    columns: requestsExportColumns,
    filters: exportFilters,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    headerTranslator: (key) => tRequestsTable(key as any),
    translators: {
      locale,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      tStatus: (key) => tRequestStatus(key as any),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      tType: (key) => tRequestType(key as any),
    },
    filenamePrefix: t("exportFilename"),
    pdfTitle: t("title"),
  });

  // Row selection state
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  // Single promote target (from row action)
  const [promoteTarget, setPromoteTarget] = useState<SearchRequestItem | null>(
    null
  );

  // Bulk promote targets
  const [bulkPromoteTargets, setBulkPromoteTargets] = useState<
    SearchRequestItem[] | null
  >(null);

  const selectedRequests = data.filter((request) => rowSelection[request.id]);
  const selectedCount = selectedRequests.length;

  function handlePromoteRow(request: SearchRequestItem) {
    setPromoteTarget(request);
  }

  function handleBulkPromote() {
    const submittedRequests = selectedRequests.filter(
      (r) => r.status === "submitted" || r.status === "accepted"
    );
    if (submittedRequests.length > 0) {
      setBulkPromoteTargets(submittedRequests);
    }
  }

  return (
    <>
      <PageHeader title={t("title")} />
      <div className="flex flex-1 flex-col gap-4 p-4">
        <RequestsFilters
          search={search}
          onSearchChange={setSearch}
          status={status}
          onStatusChange={setStatus}
          type={type}
          onTypeChange={setType}
          createdAfter={createdAfter}
          onCreatedAfterChange={setCreatedAfter}
          createdBefore={createdBefore}
          onCreatedBeforeChange={setCreatedBefore}
          searchPlaceholder={t("searchPlaceholder")}
          onExport={exportData}
          exportProgress={exportProgress}
          onExportCancel={cancelExport}
        />

        {selectedCount > 0 && (
          <div className="bg-muted flex items-center gap-3 rounded-md px-4 py-2">
            <span className="text-sm font-medium">
              {tPromote("selectedCount", { count: selectedCount })}
            </span>
            <Button size="sm" onClick={handleBulkPromote}>
              {tPromote("promoteSelected")}
            </Button>
          </div>
        )}

        <RequestsTable
          data={data}
          sorting={sorting}
          onSortingChange={onSortingChange}
          pagination={paginationState}
          onPaginationChange={onPaginationChange}
          pageCount={pageCount}
          isLoading={isLoading}
          enableRowSelection
          rowSelection={rowSelection}
          onRowSelectionChange={setRowSelection}
          onPromote={handlePromoteRow}
        />
      </div>

      {/* Single promote dialog */}
      {promoteTarget && (
        <PromoteRequestDialog
          requestId={promoteTarget.id}
          requestType={promoteTarget.type as RequestType}
          requestStatus={promoteTarget.status}
          open={!!promoteTarget}
          onOpenChange={(open) => {
            if (!open) setPromoteTarget(null);
          }}
          onSuccess={(caseId) => {
            setPromoteTarget(null);
            setRowSelection({});
            refetch();
            router.push(`/cases/${caseId}`);
          }}
        />
      )}

      {/* Bulk promote dialog */}
      {bulkPromoteTargets && (
        <PromoteBulkDialog
          requests={bulkPromoteTargets}
          open={!!bulkPromoteTargets}
          onOpenChange={(open) => {
            if (!open) setBulkPromoteTargets(null);
          }}
          onSuccess={() => {
            setBulkPromoteTargets(null);
            setRowSelection({});
            refetch();
          }}
        />
      )}
    </>
  );
}

function RequestsPage() {
  return (
    <Suspense>
      <RequestsContent />
    </Suspense>
  );
}
